import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { invalidateCatalogs, useCatalogs } from "@/lib/lookups";
import { Section } from "@/components/common/DetailFields";

const KINDS = { specialties: "Especialidades", sectors: "Sectores", relationship_types: "Tipos de relación", stages: "Etapas" };

const useEditable = (kind, cats) => {
  const [items, setItems] = useState([]);
  useEffect(() => { setItems((cats[kind] || []).map((i) => ({ ...i }))); }, [kind, cats]);
  const save = async () => {
    try {
      const { data } = await api.put(`/catalogs/${kind}`, { items });
      setItems(data.items);
      invalidateCatalogs();
      toast.success("Catálogo guardado");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return [items, setItems, save];
};

export const CatalogsSection = ({ canEdit }) => {
  const cats = useCatalogs();
  const [kind, setKind] = useState("specialties");
  const [items, setItems, save] = useEditable(kind, cats);
  const [draft, setDraft] = useState("");
  const fixed = kind === "stages";
  const move = (i, d) => setItems((s) => { const n = [...s]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; });
  const add = () => { if (draft.trim()) { setItems((s) => [...s, { label: draft.trim() }]); setDraft(""); } };
  return (
    <Section title="Catálogos" testid="config-catalogs">
      <select data-testid="catalog-kind-select" className="c-input mb-3" value={kind} onChange={(e) => setKind(e.target.value)}>
        {Object.entries(KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      {fixed && <p className="text-xs c-muted mb-2">Las etapas se pueden renombrar y reordenar.</p>}
      <ul className="space-y-1.5" data-testid="catalog-items">
        {items.map((it, i) => (
          <li key={`${it.key || "new"}-${i}`} className="flex items-center gap-1.5">
            <input data-testid={`catalog-item-input-${i}`} disabled={!canEdit} className="c-input flex-1 !py-2 text-sm" value={it.label}
              onChange={(e) => setItems((s) => s.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
            {canEdit && <>
              <button aria-label="Subir" disabled={i === 0} onClick={() => move(i, -1)} className="p-1.5 c-muted disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
              <button aria-label="Bajar" disabled={i === items.length - 1} onClick={() => move(i, 1)} className="p-1.5 c-muted disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
              {!fixed && <button data-testid={`catalog-item-remove-${i}`} aria-label="Quitar" onClick={() => setItems((s) => s.filter((_, j) => j !== i))} className="p-1.5 text-red-500"><X className="w-4 h-4" /></button>}
            </>}
          </li>
        ))}
      </ul>
      {canEdit && (
        <div className="space-y-2 mt-3">
          {!fixed && (
            <div className="flex gap-2">
              <input data-testid="catalog-new-input" className="c-input flex-1" placeholder="Nuevo elemento" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
              <button data-testid="catalog-add-btn" onClick={add} aria-label="Agregar" className="px-3 rounded-xl c-card"><Plus className="w-4 h-4" /></button>
            </div>
          )}
          <button data-testid="catalog-save-btn" onClick={save} className="c-btn-primary w-full py-3 flex items-center justify-center gap-2"><Save className="w-4 h-4" /> Guardar {KINDS[kind].toLowerCase()}</button>
        </div>
      )}
      {!canEdit && <p className="text-xs c-muted mt-2">Solo el dueño edita los catálogos.</p>}
    </Section>
  );
};

export const VocabularySection = ({ canEdit }) => {
  const cats = useCatalogs();
  const [items, setItems, save] = useEditable("vocabulary", cats);
  const [draft, setDraft] = useState("");
  const add = () => {
    const words = draft.split(",").map((w) => w.trim()).filter(Boolean);
    if (words.length) { setItems((s) => [...s, ...words.map((label) => ({ label }))]); setDraft(""); }
  };
  return (
    <Section title="Vocabulario de transcripción" testid="config-vocabulary">
      <p className="text-xs c-muted mb-2">Nombres y siglas que la transcripción debe reconocer. Los nombres de tus contactos se agregan solos.</p>
      <div className="flex flex-wrap gap-1.5" data-testid="vocab-items">
        {items.map((it, i) => (
          <span key={`${it.label}-${i}`} data-testid="vocab-chip" className="text-xs pl-2.5 pr-1 py-1 rounded-full bg-[var(--c-elev)] flex items-center gap-1">
            {it.label}
            {canEdit && <button aria-label={`Quitar ${it.label}`} data-testid={`vocab-remove-${i}`} onClick={() => setItems((s) => s.filter((_, j) => j !== i))} className="p-0.5 c-muted"><X className="w-3 h-3" /></button>}
          </span>
        ))}
      </div>
      {canEdit && (
        <div className="space-y-2 mt-3">
          <div className="flex gap-2">
            <input data-testid="vocab-new-input" className="c-input flex-1" placeholder="Palabra o varias separadas por coma" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
            <button data-testid="vocab-add-btn" onClick={add} aria-label="Agregar" className="px-3 rounded-xl c-card"><Plus className="w-4 h-4" /></button>
          </div>
          <button data-testid="vocab-save-btn" onClick={save} className="c-btn-primary w-full py-3 flex items-center justify-center gap-2"><Save className="w-4 h-4" /> Guardar vocabulario</button>
        </div>
      )}
    </Section>
  );
};
